import React, { useEffect, useMemo, useState } from 'react';

import { FilterBar, FilterSpec } from 'src/common/components/filter-bar';
import { DashboardInsightsTabs } from './tabs';
import { DashboardInsightsTabType, DashboardInsightsTabTypes } from './constants';
import { DashboardInsightsContentAll } from './content/all';
import { DashboardInsightsVisitors } from './visitors';
import { DashboardInsightsSyncedSteps } from './synced-steps';
import useDashboardActiveTab from '../helpers/active-tab.hook';
import { DashboardInsightsJourneysAll } from './journeys/all';
import {
  DashboardInsightsJourneyDownloadVisitorsCSV,
  JourneyDownloadVisitor,
} from './journeys/journey/download-visitors-csv';
import { useCurrentTags } from 'src/store/tags';
import { apiGetAllOrganizationTags } from 'src/utils/journeyApi';
import { useCurrentOrganization } from 'src/store/organization';
import { Tag } from 'src/common/interfaces/tag.interface';
import { EventBus } from 'src/common/EventBus';

function getFilterBarPlaceholder(activeTab: DashboardInsightsTabType) {
  switch (activeTab) {
    case 'content':
      return 'Search content';
    case 'journeys':
      return 'Search journeys';
    case 'visitors':
      return 'Search visitors';
    case 'synced-steps':
      return 'Search synced steps';
  }
}

type DashboardInsightsPropsInterface = {
  activeListType: string;
};

export const DashboardInsights = ({ activeListType }: DashboardInsightsPropsInterface) => {
  const { currentOrganization } = useCurrentOrganization();
  const { currentTags, setCurrentTags } = useCurrentTags();
  const [journeysToDownloadCSV, setJourneysToDownloadCSV] = useState<JourneyDownloadVisitor>({});
  useDashboardActiveTab('insights');
  const [filterSpecList, setFilterSpecList] = useState<FilterSpec[]>([]);

  const activeTab = DashboardInsightsTabTypes.includes(activeListType as DashboardInsightsTabType)
    ? (activeListType as DashboardInsightsTabType)
    : 'content';

  const isActiveTabJourneys = activeTab === 'journeys';

  const hasAtLeastOneJourneyToDownload = useMemo(
    () => Object.keys(journeysToDownloadCSV).length > 0,
    [journeysToDownloadCSV]
  );

  const fetchTags = async () => {
    const tagsResponse = await apiGetAllOrganizationTags(currentOrganization.id);
    setCurrentTags(tagsResponse.tags);
  };

  useEffect(() => {
    const formattedTags = currentTags.map((item: Tag) => ({
      value: item.id.toString(),
      label: item.name,
    }));
    setFilterSpecList((prev) => {
      const updatedList = prev.filter((spec) => spec.id !== 'tag_ids');
      return [
        ...updatedList,
        {
          id: 'tag_ids',
          label: 'Tags',
          type: 'multi-select',
          searchable: true,
          searchPlaceholder: 'Search for filter',
          options: formattedTags,
        },
      ];
    });
  }, [currentTags]);

  useEffect(() => {
    fetchTags();
    EventBus.emit('onboarding:insights-viewed');
  }, []);

  const renderInsightsContent = () => {
    if (activeTab === 'content') {
      return <DashboardInsightsContentAll />;
    }

    if (isActiveTabJourneys) {
      return (
        <DashboardInsightsJourneysAll
          onSelectionChange={(payload) => {
            const enabledJourneys = Object.entries(payload)
              .filter(([, { enable }]) => enable)
              .reduce((acc, [journeyId, { name }]) => ({ ...acc, [journeyId]: { enable: true, name } }), {});
            setJourneysToDownloadCSV(enabledJourneys);
          }}
          journeyIdsToDownloadVisitors={journeysToDownloadCSV}
        />
      );
    }

    if (activeTab === 'visitors') {
      return <DashboardInsightsVisitors />;
    }

    if (activeTab === 'synced-steps') {
      return <DashboardInsightsSyncedSteps />;
    }

    return null;
  };

  return (
    <>
      <div className='flex flex-col w-full'>
        <div className='text-bedrock-serif-h1'>Insights</div>
        <div className='mt-6 flex space-x-md divide-x divide-bedrock-gray-medium'>
          <DashboardInsightsTabs activeTab={activeTab} />
          <FilterBar
            filterSpecList={filterSpecList}
            searchPlaceholder={getFilterBarPlaceholder(activeTab)}
            widthClassname='w-[150px]'
            isDisabled={!isActiveTabJourneys}
          />
          <DashboardInsightsJourneyDownloadVisitorsCSV
            enabled={hasAtLeastOneJourneyToDownload}
            journeys={journeysToDownloadCSV}
          />
        </div>
        <div className='flex flex-col mt-6'>{renderInsightsContent()}</div>
      </div>
    </>
  );
};
